import { useCallback, useMemo, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/ui';
import { cn } from '@shared/lib/cn';
import { ApiError } from '@shared/api';
import { mediaApi, type MediaLink, type MediaScope } from '@entities/media';
import { useMediaUploads } from '../model/use-media-uploads';
import { PersistedMediaItem } from './persisted-media-item';

interface MediaPickerProps {
  scope: MediaScope;
  multiple?: boolean;
  accept?: string[];
  /** The UUIDs the form holds. */
  value: string[];
  onChange: (mediaIds: string[]) => void;
  /**
   * Original {@link MediaLink} objects from the backend payload. Used to
   * render previews / open links without an extra round-trip. Newly uploaded
   * files (whose id isn't in `links`) fall back to the local file name from
   * the in-flight upload state.
   */
  links?: MediaLink[];
  label?: string;
  hint?: string;
  disabled?: boolean;
}

export function MediaPicker({
  scope,
  multiple = false,
  accept,
  value,
  onChange,
  links,
  label,
  hint,
  disabled,
}: MediaPickerProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const linksById = useMemo(() => {
    const map = new Map<string, MediaLink>();
    for (const link of links ?? []) map.set(link.id, link);
    return map;
  }, [links]);

  const { items, addFiles, removeItem } = useMediaUploads({
    scope,
    accept,
    onUploaded: (result) => {
      onChange(multiple ? [...value, result.mediaId] : [result.mediaId]);
    },
  });

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles, disabled],
  );

  const handleRemove = (mediaId: string) => {
    onChange(value.filter((id) => id !== mediaId));
    mediaApi.remove(mediaId).catch((err) => {
      if (err instanceof ApiError && err.status === 404) return;
      console.warn('[media] remove failed', err);
    });
  };

  return (
    <div className="flex flex-col gap-3">
      {label ? <div className="text-sm font-medium">{label}</div> : null}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-input bg-muted/20 p-6 text-center transition-colors',
          dragOver && !disabled && 'border-primary bg-primary/5',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <p className="text-sm opacity-70">
          {t('media.dropHere', { kind: multiple ? t('media.files') : t('media.file') })}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          {t('media.browse')}
        </Button>
        {hint ? <p className="text-xs opacity-70">{hint}</p> : null}
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept?.join(',')}
          hidden
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {value.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {value.map((mediaId) => {
            const local = items.find((it) => it.result?.mediaId === mediaId);
            return (
              <PersistedMediaItem
                key={mediaId}
                mediaId={mediaId}
                link={linksById.get(mediaId)}
                fileName={local?.file.name}
                disabled={disabled}
                onRemove={() => handleRemove(mediaId)}
              />
            );
          })}
        </ul>
      ) : null}

      {items
        .filter((it) => it.status !== 'done' || !it.result || !value.includes(it.result.mediaId))
        .map((it) => (
          <div key={it.id} className="rounded-md border bg-background px-3 py-2 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate">{it.file.name}</span>
              <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(it.id)}>
                {it.status === 'uploading' ? t('media.cancel') : t('media.dismiss')}
              </Button>
            </div>
            {it.status === 'uploading' ? (
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${it.progress}%` }}
                />
              </div>
            ) : null}
            {it.status === 'error' ? (
              <p className="mt-1 text-xs" style={{ color: 'var(--ant-color-error)' }}>
                {it.error}
              </p>
            ) : null}
          </div>
        ))}
    </div>
  );
}
